'use client';
import cn from 'classnames';
import { useRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as braze from '@braze/web-sdk';
import { Badge, CustomScroll, FontIcon, Text } from '@intigral/shared';
import styles from './notification-center.module.sass';

type Notification = {
    imageSrc: string;
    /** E.g. `Coming soon: 1 Sept`, `New available` */
    availabilityStatus?: string;
    title: string;
    /** E.g. `Tomorrow`, `2 days ago` */
    availableSince?: string;
    href: string;
    card: any;
};

interface INotificationsCenter {
    /** Localized `Notificatations` string */
    notificationsText: string;
    notificationCards?: Notification[];
    dir?: 'ltr' | 'rtl';
    siteLogoSrc: string;
    LinkComponent: any;
    pendingNotificationsCount: number;
}
export const NotificationCenter = ({
    notificationsText,
    notificationCards = [],
    dir = 'ltr',
    siteLogoSrc,
    LinkComponent,
    pendingNotificationsCount,
}: INotificationsCenter) => {
    const notificationsRef = useRef<HTMLDivElement | null>(null);

    /**
     * Setting notification vertical position on small screen so that it covers the page completely,
     * including the header
     *
     * On biger screens notifications container is pushed down so that it is displayed under the trigger ('bell' icon)
     */
    const setNotificationsPositionAndLogImpressions = () => {
        // TODO: check for notifications div availability in `setInterval`
        setTimeout(() => {
            if (notificationCards.length > 0)
                braze.logContentCardImpressions(notificationCards.map((info) => info.card));

            const notificationsContainer = notificationsRef.current?.parentElement;

            if (window.innerWidth <= 924 && notificationsContainer) {
                notificationsContainer.style.zIndex = '100';

                // Get current transform value
                const transformValue = window.getComputedStyle(notificationsContainer).transform;

                // If the transform is in matrix form (e.g., 'matrix(a, b, c, d, e, f)')
                if (transformValue !== 'none') {
                    const matrix = new DOMMatrix(transformValue);
                    const currentTranslateX = matrix.m41; // translateX

                    // Set translateY to 0 to overwriting value set by `@radix-ui/react-dropdown-menu`,
                    // keep translateX the same
                    notificationsContainer.style.transform = `translateX(${currentTranslateX}px) translateY(0px)`;
                }
            }
        }, 250);
    };

    const renderNotification = ({ href, imageSrc, title, availabilityStatus, availableSince, card }: Notification) => {
        return (
            <DropdownMenu.Item asChild key={card.id}>
                <LinkComponent
                    href={href}
                    className={styles.notification}
                    onClick={() => {
                        braze.logContentCardClick(card);
                    }}
                >
                    <img src={imageSrc} alt={title} className={styles.image} />
                    <div className={styles.details}>
                        {availabilityStatus && (
                            <Text as="span" variant="text4">
                                {availabilityStatus}
                            </Text>
                        )}
                        <Text as="span" variant="text4" alignment="start">
                            {title}
                        </Text>
                        {availableSince && (
                            <Text as="span" variant="text5">
                                {availableSince}
                            </Text>
                        )}
                    </div>
                </LinkComponent>
            </DropdownMenu.Item>
        );
    };

    // Temporary commented the second line for braze testing
    // if (!notifications.length) return;

    return (
        <div>
            {/* <DropdownMenu.Root dir={dir} modal={false}> */}
            {/* Prevent scrollbar from adding-up to the width of page on Chrome */}
            {/* https://stackoverflow.com/questions/18548465/prevent-scrollbar-from-adding-up-to-the-width-of-page-on-chrome */}
            <DropdownMenu.Root dir={dir} onOpenChange={() => setNotificationsPositionAndLogImpressions()}>
                <DropdownMenu.Trigger
                    asChild
                    className={cn(styles.trigger, { [styles.active]: notificationCards.length > 0 })}
                >
                    <span style={{ position: 'relative' }}>
                        <FontIcon name="bell" size="medium" md="medium" />
                        {pendingNotificationsCount > 0 && (
                            <Badge fullRadius isSmall sticky>
                                {pendingNotificationsCount}
                            </Badge>
                        )}
                    </span>
                </DropdownMenu.Trigger>
                {notificationCards.length > 0 && (
                    <DropdownMenu.Content className={styles.notifications} sideOffset={8} ref={notificationsRef}>
                        <CustomScroll hidden className={styles.list}>
                            <DropdownMenu.Item className={styles.header}>
                                <img
                                    src={siteLogoSrc}
                                    className={styles['site-logo']}
                                    alt={siteLogoSrc ? 'Site logo' : ''}
                                />
                                <span>
                                    <FontIcon name="close" size="medium" />
                                </span>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item disabled className={styles.notification + ' ' + styles['heading-text']}>
                                <Text as="h2" variant="h2" margin="0">
                                    {notificationsText}
                                </Text>
                            </DropdownMenu.Item>
                            {notificationCards.map((n) => renderNotification(n))}
                        </CustomScroll>
                    </DropdownMenu.Content>
                )}
            </DropdownMenu.Root>
        </div>
    );
};
